"use client";

import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { getImageUrl } from "@/lib/database/client/database";
import type { FormField } from "@/lib/database/forms";
import type { FormEmailTemplates } from "@/lib/forms/email-templates";
import { incrementFormViewCount } from "@/features/forms/_logic/form-server-actions";
import { initializeFormData } from "@/lib/forms/utilities";
import { prefillFormDataFromViewer } from "@/features/landing-page-v2/_logic/utils/prefill-form-from-viewer";
import { getFormCached } from "@/features/landing-page-v2/_logic/form-prefetch";
import type { SubmittedFormResponse } from "@/features/forms/_logic/viewer-form";
import type { ViewerIdentity } from "@/features/landing-page-v2/_logic/actions/_shared/get-invited-page-access";

interface UseRenderableFormParams {
  isOpen: boolean;
  formId: number;
  landingPageId: number | null;
  viewer: ViewerIdentity | null;
  supabase: SupabaseClient;
  /** Called when the viewer already has a submitted response — the recap is already bundled, no extra fetch needed. */
  onSubmittedResponse: (recap: SubmittedFormResponse) => void;
  /** Called with freshly-initialized (and viewer-prefilled) form data when the form should render blank. */
  onBlankForm: (initialFormData: Record<string, unknown>) => void;
}

interface UseRenderableFormReturn {
  formFields: FormField[];
  formName: string;
  formDescription: string;
  confirmationMessage: string | null;
  emailTemplates: FormEmailTemplates | null;
  hasFileTemplate: boolean;
  willEmailFile: boolean;
  paymentEnabled: boolean;
  paymentAmountDisplay: string;
  customLogoUrl: string | null;
  isLoading: boolean;
  error: string | null;
  isFormLocked: boolean;
  /** True when the form is no longer accepting responses (manual close or passed `closes_at`). */
  isFormClosed: boolean;
  /** Visitor-facing closed copy (the admin's message, or the default); null while the form is open. */
  closedMessage: string | null;
  isRedirecting: boolean;
  /** Switches to the closed state with the given copy (a submit found the form closed after it loaded). */
  markClosed: (message: string) => void;
  setCustomLogoUrl: (url: string | null) => void;
  /** Resets all loaded-form state to its initial defaults — called when the modal closes. */
  resetForm: () => void;
}

/**
 * Loads a form's renderable schema for the public landing-page form modal
 * (via the cached `/api/forms/renderable` route) whenever the modal opens,
 * and owns all the state that load populates: fields, branding, payment
 * config, and the locked/redirecting/error outcomes. Detection of an
 * already-submitted response is handled by the caller via
 * {@link UseRenderableFormParams.onSubmittedResponse} — this hook only
 * decides which callback to invoke, not how the recap is displayed.
 *
 * @param params - See {@link UseRenderableFormParams}.
 * @returns The loaded form's state (see {@link UseRenderableFormReturn}).
 */
export function useRenderableForm({
  isOpen,
  formId,
  landingPageId,
  viewer,
  supabase,
  onSubmittedResponse,
  onBlankForm,
}: UseRenderableFormParams): UseRenderableFormReturn {
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
  const [emailTemplates, setEmailTemplates] = useState<FormEmailTemplates | null>(null);
  const [hasFileTemplate, setHasFileTemplate] = useState(false);
  const [willEmailFile, setWillEmailFile] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [paymentAmountDisplay, setPaymentAmountDisplay] = useState("");
  const [customLogoUrl, setCustomLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormLocked, setIsFormLocked] = useState(false);
  const [closedMessage, setClosedMessage] = useState<string | null>(null);
  // True from the moment we kick off an SSO sign-in redirect until the browser
  // actually navigates away — keeps a spinner up instead of flashing a blank modal.
  const [isRedirecting, setIsRedirecting] = useState(false);

  const loadForm = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // The renderable route reads the form via the service client and runs the
      // app-level access gate, so invited viewers (who the hardened RLS hides from
      // the browser client) resolve to `ok`, while non-public forms the caller
      // isn't invited to resolve to `locked`.
      const result = await getFormCached(formId, landingPageId || null);
      if (result.status === "not_found") {
        setError("Form not found.");
        return;
      }
      if (result.status === "locked") {
        // Non-public form opened by an unauthenticated visitor: send them straight
        // to sign-in (enforced SSO when a domain gates it); they return to the form
        // once authenticated. Only
        // follow an app-relative path (the server always produces one) so a future
        // contract change can't turn this into an open redirect — reject absolute,
        // protocol-relative ("//"), and javascript: targets.
        if (result.redirectTo && result.redirectTo.startsWith("/") && !result.redirectTo.startsWith("//")) {
          // Hold a "Redirecting" spinner through the navigation (the finally block
          // clears isLoading) so the modal doesn't flash an empty body first.
          setIsRedirecting(true);
          window.location.assign(result.redirectTo);
          return;
        }
        setIsFormLocked(true);
        setFormName(result.name || "");
        return;
      }
      if (result.status === "closed") {
        setFormName(result.name || "");
        setClosedMessage(result.closedMessage);
        return;
      }

      const form = result.form;

      setFormFields(form.form_fields || []);
      setFormName(form.name || "");
      setFormDescription(form.description || "");
      setConfirmationMessage(form.confirmation_message?.trim() ? form.confirmation_message : null);
      setHasFileTemplate(form.has_file_template);
      setWillEmailFile(form.will_email_file);
      setEmailTemplates(form.email_templates || {});
      setPaymentEnabled(form.payment_enabled || false);
      setPaymentAmountDisplay(form.payment_amount_display || "");

      // Resolve custom logo URL (form-level branding) if set
      if (form.custom_logo_path && form.custom_logo_path.trim() !== "") {
        getImageUrl(supabase, form.custom_logo_path)
          .then((url) => setCustomLogoUrl(url || null))
          .catch((err) => {
            console.error("useRenderableForm: error fetching logo url", err);
            setCustomLogoUrl(null);
          });
      } else {
        setCustomLogoUrl(null);
      }

      // Increment view count once per session
      const sessionKey = `form_viewed_${formId}`;
      if (typeof window !== "undefined" && !sessionStorage.getItem(sessionKey)) {
        sessionStorage.setItem(sessionKey, "true");
        incrementFormViewCount(formId).catch((err) => {
          console.error("useRenderableForm: error updating view count", err);
        });
      }

      // A single-submission form the viewer already completed: the recap is
      // already bundled in this same response (no second round-trip needed).
      if (result.submittedResponse) {
        onSubmittedResponse(result.submittedResponse);
        return;
      }

      // Initialize with defaults, then overlay logged-in viewer's identity onto
      // matching identity fields (first/last/full name, email, phone) so signed-in
      // attendees on invited landing pages don't have to re-enter their info.
      const initial = initializeFormData(form.form_fields || []);
      onBlankForm(prefillFormDataFromViewer(initial, form.form_fields || [], viewer));
    } catch (err) {
      console.error("useRenderableForm: error loading form", err);
      setError("Failed to load form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [supabase, formId, viewer, landingPageId, onSubmittedResponse, onBlankForm]);

  useEffect(() => {
    if (isOpen && formId) {
      loadForm();
    }
  }, [isOpen, formId, loadForm]);

  const markClosed = useCallback((message: string) => setClosedMessage(message), []);

  const resetForm = useCallback(() => {
    setFormFields([]);
    setFormName("");
    setFormDescription("");
    setConfirmationMessage(null);
    setHasFileTemplate(false);
    setWillEmailFile(false);
    setError(null);
    setIsFormLocked(false);
    setClosedMessage(null);
    setIsRedirecting(false);
    setCustomLogoUrl(null);
  }, []);

  return {
    formFields,
    formName,
    formDescription,
    confirmationMessage,
    emailTemplates,
    hasFileTemplate,
    willEmailFile,
    paymentEnabled,
    paymentAmountDisplay,
    customLogoUrl,
    isLoading,
    error,
    isFormLocked,
    isFormClosed: closedMessage !== null,
    closedMessage,
    isRedirecting,
    markClosed,
    setCustomLogoUrl,
    resetForm,
  };
}
